import React, { useEffect, useState } from 'react';
import { Users, Building2, MapPin, Mail, Phone, Lock, Store } from 'lucide-react';
import type { Client } from '@/types/client';
import { ClientTypeBadge } from '@/components/clients';
import api from '@/services/api';
import { useAlert } from '../../components/AlertProvider';

const MyAdherents: React.FC = () => {
  const { showError } = useAlert();
  const [adherents, setAdherents] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAdherents();
  }, []);

  const loadAdherents = async () => {
    try {
      setLoading(true);
      const response = await api.get('/clients/me/adherents');
      setAdherents(response.data);
    } catch (error: any) {
      console.error('Erreur chargement clients:', error);
      showError('Impossible de charger les clients');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-950">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between px-4 sm:px-6 py-4 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800">
        <div className="flex items-center gap-3 sm:gap-4">
          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-purple-500 via-indigo-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
            <Store className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-100">Mes Clients</h1>
            <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
              Consultez la liste des pharmacies rattachées à votre centrale
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto px-6 py-6">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
          </div>
        ) : adherents.length === 0 ? (
          <div className="bg-white dark:bg-slate-900 rounded-xl p-12 text-center border-2 border-dashed border-slate-300 dark:border-slate-700">
            <Building2 className="h-16 w-16 text-slate-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
              Aucun client
            </h3>
            <p className="text-slate-500 dark:text-slate-400 mb-6">
              Vous n'avez pas encore de clients rattachés.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {adherents.map((adherent) => (
              <div
                key={adherent.id}
                className="bg-white dark:bg-slate-900 rounded-xl p-6 border border-slate-200 dark:border-slate-800 hover:shadow-lg transition-all duration-200"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                      <Building2 className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 dark:text-slate-100 line-clamp-1" title={adherent.company_name}>
                        {adherent.company_name}
                      </h3>
                      <ClientTypeBadge type={adherent.client_type} variant="compact" />
                    </div>
                  </div>
                </div>

                <div className="space-y-2 text-sm">
                  {(adherent.first_name || adherent.last_name) && (
                    <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                      <Users className="h-4 w-4" />
                      <span>{`${adherent.first_name ?? ''} ${adherent.last_name ?? ''}`.trim()}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                    <Mail className="h-4 w-4" />
                    <span className="truncate" title={adherent.email}>{adherent.email}</span>
                  </div>
                  {adherent.phone && (
                    <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                      <Phone className="h-4 w-4" />
                      <span>{adherent.phone}</span>
                    </div>
                  )}
                  {(adherent.city || adherent.postal_code) && (
                    <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                      <MapPin className="h-4 w-4" />
                      <span>{adherent.postal_code} {adherent.city}</span>
                    </div>
                  )}
                </div>

                <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-800">
                    <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                        {adherent.use_central_purchasing_bpu ? (
                            <div className="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 px-2 py-1 rounded">
                                <Lock className="h-3 w-3" />
                                <span>Utilise votre BPU</span>
                            </div>
                        ) : (
                            <div className="flex items-center gap-1.5 text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-2 py-1 rounded">
                                <span>BPU personnalisé</span>
                            </div>
                        )}
                    </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyAdherents;
